/**
 * Date Column Pattern
 * 
 * Creates date columns with smart formatting and relative time options.
 */

import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import type { ColumnDef } from '@tanstack/react-table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@etherisc/ui-kit';
import { TABLE_SETTINGS } from '../../constants/tableSettings';
import type { DateColumnOptions } from './types';

/**
 * Creates a date column with smart defaults and optional customization
 * 
 * @param accessor - The data accessor key  
 * @param options - Optional customization options
 * @returns ColumnDef for TanStack Table
 * 
 * @example
 * // Basic usage - uses default format
 * dateColumn('createdAt')
 * 
 * // With customization
 * dateColumn('createdAt', {
 *   format: 'dd/MM/yyyy',
 *   showTime: true,
 *   relative: true
 * })
 */
export function dateColumn<T>(
  accessor: keyof T,
  options: DateColumnOptions = {}
): ColumnDef<T> {
  const {
    header = String(accessor),
    sortable = true,
    resizable = true,
    format: dateFormat = TABLE_SETTINGS.dateFormat.default,
    showTime = false,
    relative = TABLE_SETTINGS.dateFormat.relative,
    emptyDisplay = TABLE_SETTINGS.text.emptyDisplay,
    width,
    maxWidth,
    minWidth,
  } = options;

  return {
    id: String(accessor),
    header,
    accessorKey: accessor,
    enableSorting: sortable,
    enableResizing: resizable,
    size: width,
    maxSize: maxWidth,
    minSize: minWidth,
    cell: ({ row }) => {
      const dateValue = row.original[accessor];
      
      // Handle null/undefined dates
      if (!dateValue) {
        return emptyDisplay;
      }
      
      // Convert to Date object if needed
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue as string);
      
      // Handle invalid dates
      if (isNaN(date.getTime())) {
        return emptyDisplay;
      }
      
      // Return relative time with tooltip if requested
      if (relative || dateFormat === 'relative') {
        const relativeTime = formatDistanceToNow(date, { addSuffix: true });
        const fullDateTime = format(date, TABLE_SETTINGS.dateFormat.withTime);
        
        return React.createElement(
          Tooltip,
          null,
          React.createElement(
            TooltipTrigger,
            { asChild: true },
            React.createElement('span', { className: 'cursor-help' }, relativeTime)
          ),
          React.createElement(
            TooltipContent,
            null,
            fullDateTime
          )
        );
      }
      
      // Use time format if showTime is true
      const formatString = showTime 
        ? TABLE_SETTINGS.dateFormat.withTime
        : dateFormat;
        
      return format(date, formatString);
    },
  };
}

/**
 * Creates a simple date column (date only, no time)
 */
export function simpleDateColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return dateColumn(accessor, {
    header,
    showTime: false,
    relative: false,
  });
}

/**
 * Creates a datetime column (date + time)
 */
export function dateTimeColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return dateColumn(accessor, {
    header,
    showTime: true,
    relative: false,
  });
}

/**
 * Creates a relative time column ("2 days ago") with tooltip showing full date/time
 */
export function relativeTimeColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return dateColumn(accessor, {
    header,
    format: 'relative',
  });
} 